import SearchIcon from '@mui/icons-material/Search'
import {
    Box,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
} from '@mui/material'
import { ClearIcon } from '@mui/x-date-pickers'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import TruncatedText from '../../../Common/TruncatedText'

interface UploadManagerSearchColumn {
    attributeName: string
    columnName: string
    searchable?: boolean
}

interface UploadManagerSearchProps {
    onSearchChange: (value: string) => void
    onColumnSelect: (col: UploadManagerSearchColumn | null) => void
    searchableColumns: UploadManagerSearchColumn[]
    selectedColumn: UploadManagerSearchColumn | null
    searchValue: string
    triggerSearch: (searchText?: string) => void
}

const UploadManagerSearch = ({
    onSearchChange,
    onColumnSelect,
    searchableColumns,
    selectedColumn,
    searchValue,
    triggerSearch,
}: UploadManagerSearchProps): React.ReactElement => {
    const { t } = useTranslation()

    const handleSearchTextChange = useCallback(
        (text: string) => {
            onSearchChange(text)
            if (text === '') {
                setTimeout(() => triggerSearch(''), 100)
            }
        },
        [onSearchChange, triggerSearch],
    )

    const handleSearchColumnChange = useCallback(
        (column: UploadManagerSearchColumn | null) => {
            onColumnSelect(column)
        },
        [onColumnSelect],
    )

    const handleTriggerSearch = useCallback(() => {
        triggerSearch(searchValue)
    }, [triggerSearch, searchValue])

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white',
                borderRadius: '25px',
            }}
        >
            <Box sx={{ width: '160px', borderRight: '2px solid #d3d3d3' }}>
                <Select
                    id="select-search-column"
                    value={selectedColumn?.attributeName ?? ''}
                    onChange={(e) =>
                        handleSearchColumnChange(
                            searchableColumns.find(
                                (col: UploadManagerSearchColumn) =>
                                    col.attributeName === e.target.value,
                            ) ?? null,
                        )
                    }
                    renderValue={(selected) => {
                        if (!selected) {
                            return (
                                <TruncatedText
                                    style={{ fontSize: '14px', opacity: '0.4' }}
                                >
                                    Select
                                </TruncatedText>
                            )
                        }
                        return searchableColumns.find(
                            (col: UploadManagerSearchColumn) =>
                                col.attributeName === selected,
                        )?.columnName
                    }}
                    size="small"
                    sx={{
                        width: '100%',
                        fontSize: '14px',
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 250,
                                fontSize: '14px',
                            },
                        },
                    }}
                >
                    {searchableColumns?.map((c: UploadManagerSearchColumn) => (
                        <MenuItem
                            sx={{
                                fontSize: '14px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                            key={c.attributeName}
                            id={c.attributeName}
                            value={c.attributeName}
                        >
                            <TruncatedText
                                text={c.columnName}
                                sx={{
                                    fontSize: '14px',
                                }}
                            />
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            <Box sx={{ width: '180px' }}>
                <TextField
                    id="common-search-input"
                    sx={{
                        padding: 0,
                        '& fieldset': { border: 'none' },
                        '& .MuiInputBase-root': { paddingLeft: '5px' },
                    }}
                    placeholder={t('search')}
                    value={searchValue}
                    onChange={(e) => handleSearchTextChange(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            handleTriggerSearch()
                        }
                    }}
                    className="table-search-bar"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment
                                position="end"
                                sx={{ marginRight: '-8px' }}
                            >
                                {searchValue && (
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleSearchTextChange('')
                                        }
                                        sx={{
                                            visibility: 'hidden',
                                            '.Mui-focused &': {
                                                visibility: 'visible',
                                            },
                                            '.MuiInputBase-root:hover &': {
                                                visibility: 'visible',
                                            },
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <IconButton
                                    id="common-search-clear-button"
                                    sx={{
                                        '&:hover': { background: 'none' },
                                        padding: '6px',
                                    }}
                                    onClick={handleTriggerSearch}
                                >
                                    <SearchIcon sx={{ color: 'primary' }} />
                                </IconButton>
                            </InputAdornment>
                        ),
                        style: { height: 32 },
                    }}
                />
            </Box>
        </Box>
    )
}

export default UploadManagerSearch
